import { useCallback, useEffect, useState } from "react";
import { Check, X, CalendarOff, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0">
    <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-900">{value || "–"}</span>
  </div>
);

export default function Approvals() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const leader = isLeader(user?.role);
  const [absences, setAbsences] = useState([]);
  const [staff, setStaff] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/absences");
      setAbsences(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setStaff(data)).catch(() => {});
  }, [load]);

  const staffName = (id) => staff.find((s) => s.id === id)?.name || t("common.unknown");

  const decide = async (a, status) => {
    try {
      await api.put(`/absences/${a.id}`, { status });
      toast.success(status === "approved" ? t("absPage.approved") : t("absPage.rejected"));
      setActive(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pending = absences.filter((a) => a.status === "pending");
  const handled = absences.filter((a) => a.status !== "pending");

  return (
    <div data-testid="approvals-page">
      <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("approvals.title")}</h1>
      <p className="mb-4 text-sm text-slate-500">
        {t("approvals.pending", { count: pending.length })}
      </p>

      <div className="space-y-2">
        {pending.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="approvals-empty">
            {t("approvals.empty")}
          </div>
        )}
        {[...pending, ...handled].map((a) => (
          <button
            key={a.id}
            data-testid={`approval-row-${a.id}`}
            onClick={() => setActive(a)}
            className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors duration-200 hover:border-blue-300 hover:bg-slate-50"
          >
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                a.status === "approved"
                  ? "bg-green-100 text-green-700"
                  : a.status === "rejected"
                  ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700"
              }`}
              data-testid={`approval-status-icon-${a.id}`}
            >
              {a.status === "approved" ? <Check className="h-4 w-4" /> : a.status === "rejected" ? <X className="h-4 w-4" /> : "!"}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">{staffName(a.user_id)}</p>
              <p className="text-xs text-slate-500">
                {t(`absType.${a.type}`)} · {a.start?.slice(0, 10)} – {a.end?.slice(0, 10)} · {t(`absStatus.${a.status}`)}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="approval-dialog" className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <CalendarOff className="h-4 w-4 text-orange-600" />
              Frånvaroansökan            </DialogTitle>
          </DialogHeader>
          {active && (
            <>
              <div data-testid="approval-detail">
                <Row label={t("common.person")} value={staffName(active.user_id)} />
                <Row label={t("common.type")} value={t(`absType.${active.type}`)} />
                <Row label={t("common.from")} value={active.start?.slice(0, 10)} />
                <Row label={t("common.to")} value={active.end?.slice(0, 10)} />
                <Row label={t("common.note")} value={active.note} />
                <Row label={t("common.status")} value={t(`absStatus.${active.status}`)} />
                <Row label={t("common.reportedAt")} value={active.created_at?.slice(0, 16).replace("T", " ")} />
              </div>
              {leader && (
                <div className="flex gap-2 pt-1">
                  <Button
                    data-testid="approval-approve-button"
                    onClick={() => decide(active, "approved")}
                    className="h-12 flex-1 bg-green-600 text-base font-semibold text-white transition-colors duration-200 hover:bg-green-700"
                  >
                    <Check className="mr-1.5 h-4 w-4" /> {t("absPage.approve")}
                  </Button>
                  <Button
                    data-testid="approval-reject-button"
                    onClick={() => decide(active, "rejected")}
                    variant="outline"
                    className="h-12 flex-1 border-red-200 bg-white text-base font-semibold text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <X className="mr-1.5 h-4 w-4" /> {t("absPage.reject")}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
